import { supabase } from './supabase';

export type PushSubscriptionStatus =
  | 'unsupported'
  | 'permission_not_granted'
  | 'missing'
  | 'active';

function urlBase64ToArrayBuffer(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    output[i] = rawData.charCodeAt(i);
  }

  return output.buffer;
}

export function isPushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;

  return navigator.serviceWorker.register('/sw.js');
}

/*
 * Controllo puramente locale.
 *
 * NON interroga Supabase e NON crea una nuova subscription.
 * Può quindi essere eseguito ogni volta che l'app torna
 * in primo piano senza consumare chiamate al database.
 */
export async function getPushSubscriptionStatus(): Promise<PushSubscriptionStatus> {
  if (!isPushSupported()) {
    return 'unsupported';
  }

  if (Notification.permission !== 'granted') {
    return 'permission_not_granted';
  }

  const registration = await registerServiceWorker();

  if (!registration) {
    return 'unsupported';
  }

  const subscription =
    await registration.pushManager.getSubscription();

  if (!subscription) {
    return 'missing';
  }

  return 'active';
}

/*
 * Salva la subscription corrente e la rende l'unica
 * subscription attiva per questo utente.
 */
async function saveCurrentSubscription(
  userId: string,
  subscription: PushSubscription
): Promise<void> {
  const json = subscription.toJSON();

  const endpoint = json.endpoint;
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;

  if (!endpoint || !p256dh || !auth) {
    throw new Error('Subscription push incompleta.');
  }

  const now = new Date().toISOString();

  /*
   * Prima salviamo/riattiviamo la subscription corrente.
   */
  const { error: upsertError } = await supabase
    .from('push_subscriptions')
    .upsert(
      {
        user_id: userId,
        endpoint,
        p256dh,
        auth,
        user_agent: navigator.userAgent,
        last_seen_at: now,
        revoked_at: null
      },
      {
        onConflict: 'endpoint'
      }
    );

  if (upsertError) {
    throw upsertError;
  }

  /*
   * Modalità SINGLE DEVICE:
   *
   * tutte le altre subscription dello stesso utente
   * vengono revocate.
   *
   * Non vengono cancellate dal database, così rimane
   * lo storico e possono essere diagnosticate.
   */
  const { error: revokeError } = await supabase
    .from('push_subscriptions')
    .update({
      revoked_at: now
    })
    .eq('user_id', userId)
    .is('revoked_at', null)
    .neq('endpoint', endpoint);

  if (revokeError) {
    throw revokeError;
  }
}

export async function enablePushNotifications(
  userId: string
): Promise<void> {
  if (!isPushSupported()) {
    throw new Error(
      'Le notifiche push non sono supportate su questo dispositivo/browser.'
    );
  }

  const vapidPublicKey =
    import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

  if (!vapidPublicKey) {
    throw new Error('Configura VITE_VAPID_PUBLIC_KEY.');
  }

  /*
   * Questa funzione viene chiamata da un pulsante,
   * quindi la richiesta del permesso avviene a seguito
   * di un'azione esplicita dell'utente.
   */
  const permission = await Notification.requestPermission();

  if (permission !== 'granted') {
    throw new Error('Permesso notifiche non concesso.');
  }

  const registration = await registerServiceWorker();

  if (!registration) {
    throw new Error('Service worker non disponibile.');
  }

  /*
   * Se esiste già una subscription valida la riutilizziamo.
   * Altrimenti ne creiamo una nuova.
   */
  const existing =
    await registration.pushManager.getSubscription();

  const subscription =
    existing ||
    await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey:
        urlBase64ToArrayBuffer(vapidPublicKey)
    });

  await saveCurrentSubscription(
    userId,
    subscription
  );
}

/*
 * Sincronizzazione automatica.
 *
 * Non chiede permessi e non crea automaticamente
 * una nuova subscription.
 *
 * Se la subscription esiste, viene aggiornata su Supabase
 * e diventa l'unica subscription attiva dell'utente.
 *
 * Se il permesso è granted ma la subscription è sparita,
 * restituisce "missing": sarà l'interfaccia a chiedere
 * all'utente di riattivarla con un tap.
 */
export async function syncExistingPushSubscription(
  userId: string
): Promise<PushSubscriptionStatus> {
  if (!isPushSupported()) {
    return 'unsupported';
  }

  if (Notification.permission !== 'granted') {
    return 'permission_not_granted';
  }

  const registration = await registerServiceWorker();

  if (!registration) {
    return 'unsupported';
  }

  const subscription =
    await registration.pushManager.getSubscription();

  if (!subscription) {
    return 'missing';
  }

  await saveCurrentSubscription(
    userId,
    subscription
  );

  return 'active';
}
